import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MDBBadge,
  MDBBtn,
  MDBBtnGroup,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCol,
  MDBIcon,
  MDBInput,
  MDBRow,
  MDBSelect,
  MDBSpinner,
  MDBSwitch,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from 'mdb-react-ui-kit';
import { generateClient } from 'aws-amplify/api';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { captureError } from '../../utils/sentryHelpers';
import { transformMoney } from '../../utils/money';
import { getLocalizedText } from '../../utils/localizedText';
import { getLanguageText } from '../../utils/mediathek';
import {
  BOOTH_PRODUCT_TYPES,
  compareBoothNumbers,
  normalizeBoothNumber,
  normalizeVariant,
} from '../../utils/boothMap';
import { ConfirmModal, ErrorList, FlashToast, useFlashToast } from '../mediathek/MediathekShared';

const client = generateClient();

const PAGE_LIMIT = 500;
const MAX_EVENT_NAMES = 3;

const EMPTY_DELETE_CHECK = {
  checking: false,
  orders: [],
  reservationCount: 0,
  eventProductIds: [],
  componentIds: [],
  industryLinkIds: [],
};

const BOOTHS_BY_TYPE_QUERY = /* GraphQL */ `
  query BoothProductsByType($type: String!, $limit: Int, $nextToken: String) {
    productsByType(type: $type, limit: $limit, nextToken: $nextToken) {
      items {
        id
        sku
        type
        price
        stock
        boothNumber
        boothVariant
        boothVariantAliases
        boothMatchRule
        boothPriceMode
        boothArea
        boothWidth
        boothDepth
        boothHeight
        name {
          language
          text
        }
        image {
          main {
            fileName
          }
        }
        boothDimensionFile {
          fileName
          s3Path
        }
        booth3DModel {
          fileName
          s3Path
        }
        eventProducts(limit: 200) {
          items {
            id
            price
            stock
            event {
              id
              name {
                language
                text
              }
            }
          }
        }
      }
      nextToken
    }
  }
`;

const PRODUCT_DELETE_CHECK_QUERY = /* GraphQL */ `
  query BoothDeleteCheck($id: ID!) {
    getProduct(id: $id) {
      id
      orderLineItems(limit: 1000) {
        items {
          id
          order {
            orderNumber
          }
        }
      }
      eventProducts(limit: 1000) {
        items {
          id
          stockReservations(limit: 1000) {
            items {
              id
            }
          }
        }
      }
      stockReservations(limit: 1000) {
        items {
          id
        }
      }
      components(limit: 1000) {
        items {
          id
        }
      }
      availableInIndustries(limit: 1000) {
        items {
          id
        }
      }
    }
  }
`;

const DELETE_PRODUCT_COMPONENT_MUTATION = /* GraphQL */ `
  mutation DeleteBoothProductComponent($input: DeleteProductComponentInput!) {
    deleteProductComponent(input: $input) {
      id
    }
  }
`;

const DELETE_PRODUCT_INDUSTRY_MUTATION = /* GraphQL */ `
  mutation DeleteBoothProductIndustry($input: DeleteAvailableProductsInIndustriesInput!) {
    deleteAvailableProductsInIndustries(input: $input) {
      id
    }
  }
`;

const DELETE_EVENT_PRODUCT_MUTATION = /* GraphQL */ `
  mutation DeleteBoothEventProduct($input: DeleteEventProductInput!) {
    deleteEventProduct(input: $input) {
      id
    }
  }
`;

const DELETE_PRODUCT_MUTATION = /* GraphQL */ `
  mutation DeleteBoothProduct($input: DeleteProductInput!) {
    deleteProduct(input: $input) {
      id
    }
  }
`;

/**
 * client.graphql that keeps partial data. Amplify v6 rejects on any GraphQL error, e.g. when an
 * EventProduct references a deleted event (EventProduct.event is non-null, so AppSync nulls that
 * item and reports an error). The orphan items arrive as null and are filtered by the callers.
 */
async function graphqlAllowPartial(options, rootField, operation) {
  try {
    return await client.graphql(options);
  } catch (error) {
    if (!error?.data?.[rootField]) throw error;
    captureError(error, {
      feature: 'booth-admin',
      operation,
      consoleMessage: 'Booth query returned partial data',
      context: { variables: options.variables },
      tags: { component: 'AdminAllBooths', partial: 'true' },
    });
    return error;
  }
}

/** All products of one type (nextToken loop). */
async function fetchProductsOfType(type) {
  const items = [];
  let nextToken = null;
  do {
    const response = await graphqlAllowPartial(
      { query: BOOTHS_BY_TYPE_QUERY, variables: { type, limit: PAGE_LIMIT, nextToken } },
      'productsByType',
      'fetch-booth-products'
    );
    const page = response.data.productsByType;
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
}

/** German name first (booths are maintained in German), else the current language. */
function boothName(product) {
  return getLanguageText(product.name, 'de') || getLocalizedText(product.name);
}

function formatMeters(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

/** Match rule of a product (Product.boothMatchRule, AWSJSON string or object) as an object, else null. */
function matchRuleOf(product) {
  let value = product?.boothMatchRule;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      captureError(error, {
        feature: 'booth-admin',
        operation: 'parse-booth-match-rule',
        consoleMessage: 'Error reading the match rule of a booth product',
        context: { productId: product?.id },
        tags: { component: 'AdminAllBooths' },
      });
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/**
 * Sort rank: single booths (booth number) first, then offers with an offer code, then offers with only a match
 * rule (they cover the plan booths by features), then the rest.
 */
function planRank(product) {
  if (normalizeBoothNumber(product.boothNumber)) return 0;
  if (normalizeVariant(product.boothVariant)) return 1;
  if (matchRuleOf(product)) return 2;
  return 3;
}

function sortBooths(a, b) {
  const aRank = planRank(a);
  const bRank = planRank(b);
  if (aRank !== bRank) return aRank - bRank;
  if (aRank < 2) {
    const key = aRank === 0 ? 'boothNumber' : 'boothVariant';
    const byKey = compareBoothNumbers(String(a[key]).trim(), String(b[key]).trim());
    if (byKey !== 0) return byKey;
  }
  return boothName(a).localeCompare(boothName(b), 'de', { numeric: true });
}

/** Offer code and aliases of a product match the (normalized) query. */
function variantMatches(product, normalizedQuery) {
  if (!normalizedQuery) return false;
  return [product.boothVariant, ...(Array.isArray(product.boothVariantAliases) ? product.boothVariantAliases : [])]
    .some((code) => normalizeVariant(code).includes(normalizedQuery));
}

function Indicator({ present, label }) {
  return (
    <MDBIcon
      fas
      icon={present ? 'check' : 'minus'}
      className={present ? 'text-success' : 'text-muted'}
      title={label}
      aria-label={label}
    />
  );
}

export default function AdminAllBooths() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [onlyPlanBooths, setOnlyPlanBooths] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [toast, setToast] = useFlashToast();

  // Delete flow
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCheck, setDeleteCheck] = useState(EMPTY_DELETE_CHECK);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const loadBooths = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const lists = await Promise.all(BOOTH_PRODUCT_TYPES.map((type) => fetchProductsOfType(type)));
      setProducts(lists.flat());
    } catch (error) {
      console.warn('Error loading booth products', error);
      captureError(error, {
        feature: 'booth-admin',
        operation: 'fetch-booth-products',
        consoleMessage: 'Error loading booth products',
        tags: { component: 'AdminAllBooths' },
      });
      setLoadError('The booths could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooths();
  }, [loadBooths]);

  const visibleBooths = useMemo(() => {
    const query = search.trim().toLowerCase();
    const normalizedQuery = normalizeBoothNumber(search);
    return products
      // hall-plan booths: single booths (booth number), offers with an offer code or a match rule
      .filter((product) => !onlyPlanBooths || planRank(product) < 3)
      .filter((product) => !typeFilter || product.type === typeFilter)
      .filter((product) => {
        if (!query) return true;
        if (normalizedQuery && normalizeBoothNumber(product.boothNumber).includes(normalizedQuery)) return true;
        if (variantMatches(product, normalizedQuery)) return true;
        if ((product.sku || '').toLowerCase().includes(query)) return true;
        return (product.name || []).some((entry) => (entry?.text || '').toLowerCase().includes(query));
      })
      .sort(sortBooths);
  }, [products, onlyPlanBooths, typeFilter, search]);

  const typeLabel = (type) => {
    if (type === 'event_booth') return t('Event booths');
    if (type === 'event_space') return t('Event spaces');
    return type;
  };

  const eventNames = (product) => {
    const names = (product.eventProducts?.items || [])
      .filter((item) => item?.event)
      .map((item) => getLocalizedText(item.event.name))
      .filter(Boolean);
    if (names.length <= MAX_EVENT_NAMES) return { short: names.join(', '), full: names.join(', ') };
    return {
      short: `${names.slice(0, MAX_EVENT_NAMES).join(', ')} ${t('+{{count}} more', { count: names.length - MAX_EVENT_NAMES })}`,
      full: names.join(', '),
    };
  };

  const openDelete = async (product) => {
    setDeleteTarget(product);
    setDeleteError(null);
    setDeleteCheck({ ...EMPTY_DELETE_CHECK, checking: true });
    try {
      const response = await client.graphql({ query: PRODUCT_DELETE_CHECK_QUERY, variables: { id: product.id } });
      const dbProduct = response.data.getProduct;
      const orders = (dbProduct?.orderLineItems?.items || [])
        .filter((item) => item?.order?.orderNumber)
        .map((item) => item.order.orderNumber);
      const eventProducts = (dbProduct?.eventProducts?.items || []).filter(Boolean);
      const eventProductIds = eventProducts.map((item) => item.id);
      // Holds / options of the booth (per event and legacy product-level ones) block the delete
      const reservationIds = new Set(
        [
          ...eventProducts.flatMap((item) => item.stockReservations?.items || []),
          ...(dbProduct?.stockReservations?.items || []),
        ]
          .filter(Boolean)
          .map((reservation) => reservation.id)
      );
      const componentIds = (dbProduct?.components?.items || []).filter(Boolean).map((item) => item.id);
      const industryLinkIds = (dbProduct?.availableInIndustries?.items || []).filter(Boolean).map((item) => item.id);
      setDeleteCheck({
        checking: false,
        orders,
        reservationCount: reservationIds.size,
        eventProductIds,
        componentIds,
        industryLinkIds,
      });
    } catch (error) {
      console.warn('Error checking booth orders', error);
      captureError(error, {
        feature: 'booth-admin',
        operation: 'check-booth-orders',
        consoleMessage: 'Error checking booth orders',
        context: { productId: product.id },
        tags: { component: 'AdminAllBooths' },
      });
      setDeleteCheck({ ...EMPTY_DELETE_CHECK, failed: true });
      setDeleteError('The orders of this booth could not be checked.');
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
    setDeleteCheck(EMPTY_DELETE_CHECK);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    // Related rows first (included components, industry links, event links), the product last
    const steps = [
      ['componentIds', DELETE_PRODUCT_COMPONENT_MUTATION],
      ['industryLinkIds', DELETE_PRODUCT_INDUSTRY_MUTATION],
      ['eventProductIds', DELETE_EVENT_PRODUCT_MUTATION],
    ];
    try {
      for (const [key, mutation] of steps) {
        for (const id of deleteCheck[key]) {
          await client.graphql({ query: mutation, variables: { input: { id } } });
          // keep the check in sync so a retry after a partial failure does not delete twice
          setDeleteCheck((prev) => ({ ...prev, [key]: prev[key].filter((entry) => entry !== id) }));
        }
      }
      await client.graphql({ query: DELETE_PRODUCT_MUTATION, variables: { input: { id: deleteTarget.id } } });
      setProducts((prev) => prev.filter((product) => product.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteCheck(EMPTY_DELETE_CHECK);
      setToast('Booth deleted.');
    } catch (error) {
      console.warn('Error deleting booth', error);
      captureError(error, {
        feature: 'booth-admin',
        operation: 'delete-booth',
        consoleMessage: 'Error deleting booth',
        context: {
          productId: deleteTarget.id,
          eventProductIds: deleteCheck.eventProductIds,
          componentIds: deleteCheck.componentIds,
          industryLinkIds: deleteCheck.industryLinkIds,
        },
        tags: { component: 'AdminAllBooths' },
      });
      setDeleteError('The booth could not be deleted.');
    } finally {
      setDeleting(false);
    }
  };

  const deleteBlocked =
    deleteCheck.checking || deleteCheck.failed || deleteCheck.orders.length > 0 || deleteCheck.reservationCount > 0;

  const deleteMessage = (
    <div>
      {deleteCheck.checking ? (
        <div className="text-center">
          <MDBSpinner size="sm" className="me-2" />
          {t('Checking orders...')}
        </div>
      ) : deleteCheck.orders.length > 0 ? (
        <>
          <p className="text-danger">{t('This product has already been ordered and cannot be deleted.')}</p>
          <p className="mb-0">
            {t('Order numbers')}: {deleteCheck.orders.join(', ')}
          </p>
        </>
      ) : deleteCheck.reservationCount > 0 ? (
        <>
          <p className="text-danger">
            {t('This booth has {{count}} stock reservation(s) (holds or options) and cannot be deleted.', {
              count: deleteCheck.reservationCount,
            })}
          </p>
          <p className="mb-0">
            {t('Remove the reservations first under')}{' '}
            <Link to="/admin/stockreservations">{t('Stock Reservations')}</Link>.
          </p>
        </>
      ) : (
        <>
          <p>
            {t('Delete booth {{boothNumber}} ({{name}})?', {
              boothNumber: deleteTarget?.boothNumber || deleteTarget?.boothVariant || '-',
              name: deleteTarget ? boothName(deleteTarget) : '',
            })}
          </p>
          {deleteCheck.eventProductIds.length > 0 && (
            <p className="mb-0 text-muted small">
              {t('It is also removed from {{count}} event(s).', { count: deleteCheck.eventProductIds.length })}
            </p>
          )}
          {deleteCheck.componentIds.length + deleteCheck.industryLinkIds.length > 0 && (
            <p className="mb-0 text-muted small">
              {t('Its included components and industry assignments are deleted as well.')}
            </p>
          )}
        </>
      )}
      {deleteError && <div className="mt-3"><ErrorList errors={[deleteError]} /></div>}
    </div>
  );

  return (
    <>
      <FlashToast message={toast} onClose={() => setToast(null)} />
      <MDBRow>
        <MDBCol>
          <MDBCard>
            <MDBCardBody>
              <MDBCardTitle className="d-flex flex-wrap align-items-center gap-2">
                <span>{t('Booths')}</span>
                <Link to="/admin/booth/create">
                  <MDBBtn className="mx-2 my-0 py-0" color="tertiary" rippleColor="light">
                    {t('Create booth')}
                  </MDBBtn>
                </Link>
              </MDBCardTitle>
              <p className="text-muted small mb-3">
                {t('Booths are products of type event booth or event space. Booths with a booth number appear on the hall plan.')}
                {' '}
                {t('An offer (offer code) stands for all booths of the hall plan with this plan code, an offer with a match rule for all booths with matching features; the exhibitor picks the booth number.')}
              </p>

              <MDBRow className="g-3 align-items-center mb-3">
                <MDBCol md="5">
                  <MDBInput
                    label={t('Search (booth number, offer code, name, SKU)')}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="3">
                  <MDBSelect
                    label={t('Type')}
                    value={typeFilter}
                    onChange={(entry) => setTypeFilter(entry?.value || '')}
                    data={[
                      { text: t('All'), value: '' },
                      { text: t('Event booths'), value: 'event_booth' },
                      { text: t('Event spaces'), value: 'event_space' },
                    ]}
                  />
                </MDBCol>
                <MDBCol md="4">
                  <MDBSwitch
                    id="onlyPlanBooths"
                    label={t('Only hall-plan booths (with booth number, offer code or match rule)')}
                    checked={onlyPlanBooths}
                    onChange={(e) => setOnlyPlanBooths(e.target.checked)}
                  />
                </MDBCol>
              </MDBRow>

              {loadError && <ErrorList errors={[loadError]} />}

              {loading ? (
                <div className="text-center my-5">
                  <MDBSpinner role="status" />
                </div>
              ) : (
                <>
                  <p className="small text-muted mb-2">
                    {t('{{shown}} of {{total}} booth products', { shown: visibleBooths.length, total: products.length })}
                  </p>
                  <div className="table-responsive">
                    <MDBTable align="middle" small hover>
                      <MDBTableHead>
                        <tr>
                          <th>{t('Booth number')}</th>
                          <th>{t('Offer code')}</th>
                          <th>{t('Name')}</th>
                          <th>{t('SKU')}</th>
                          <th>{t('Type')}</th>
                          <th className="text-end">{t('m²')}</th>
                          <th>{t('W × D (m)')}</th>
                          <th className="text-end">{t('Price')}</th>
                          <th className="text-center">{t('Image')}</th>
                          <th className="text-center">{t('PDF')}</th>
                          <th className="text-center">{t('3D')}</th>
                          <th>{t('Events')}</th>
                          <th>{t('Actions')}</th>
                        </tr>
                      </MDBTableHead>
                      <MDBTableBody>
                        {visibleBooths.length === 0 && (
                          <tr>
                            <td colSpan={13} className="text-center text-muted py-4">
                              {t('No booths found.')}
                            </td>
                          </tr>
                        )}
                        {visibleBooths.map((product) => {
                          const events = eventNames(product);
                          const width = formatMeters(product.boothWidth);
                          const depth = formatMeters(product.boothDepth);
                          const aliases = (Array.isArray(product.boothVariantAliases) ? product.boothVariantAliases : [])
                            .map((alias) => String(alias || '').trim())
                            .filter(Boolean);
                          return (
                            <tr key={product.id}>
                              <td className="fw-bold text-nowrap">{product.boothNumber?.trim() || '-'}</td>
                              <td className="text-nowrap">
                                {product.boothVariant?.trim() ? (
                                  <>
                                    <span className="fw-bold">{product.boothVariant.trim()}</span>
                                    {aliases.length > 0 && (
                                      <div className="small text-muted">
                                        {t('Aliases: {{list}}', { list: aliases.join(', ') })}
                                      </div>
                                    )}
                                  </>
                                ) : null}
                                {!product.boothVariant?.trim() && matchRuleOf(product) && (
                                  <MDBBadge color="info" light>{t('Rule offer')}</MDBBadge>
                                )}
                                {!product.boothVariant?.trim() && !matchRuleOf(product) && '-'}
                                {product.boothPriceMode === 'perSqm' && (
                                  <div className="small text-muted">{t('Price per m²')}</div>
                                )}
                              </td>
                              <td>{boothName(product)}</td>
                              <td className="text-nowrap">{product.sku}</td>
                              <td className="text-nowrap">{typeLabel(product.type)}</td>
                              <td className="text-end">{formatMeters(product.boothArea)}</td>
                              <td className="text-nowrap">{width || depth ? `${width || '?'} × ${depth || '?'}` : ''}</td>
                              <td className="text-end text-nowrap">
                                {product.price !== null && product.price !== undefined ? transformMoney(product.price) : ''}
                              </td>
                              <td className="text-center">
                                <Indicator present={Boolean(product.image?.main?.fileName)} label={t('Image')} />
                              </td>
                              <td className="text-center">
                                <Indicator present={Boolean(product.boothDimensionFile?.s3Path)} label={t('Dimension drawing (PDF)')} />
                              </td>
                              <td className="text-center">
                                <Indicator present={Boolean(product.booth3DModel?.s3Path)} label={t('3D model')} />
                              </td>
                              <td className="small" title={events.full} style={{ maxWidth: '260px' }}>
                                {events.short}
                              </td>
                              <td>
                                <MDBBtnGroup>
                                  <Link to={`/admin/booth/${product.id}`}>
                                    <MDBBtn color="info" size="sm" title={t('Edit')} aria-label={t('Edit')}>
                                      <MDBIcon fas icon="edit" />
                                    </MDBBtn>
                                  </Link>
                                  <Link to={`/admin/product/${product.id}`}>
                                    <MDBBtn color="secondary" size="sm" title={t('Edit as product')} aria-label={t('Edit as product')}>
                                      <MDBIcon fas icon="box" />
                                    </MDBBtn>
                                  </Link>
                                  <MDBBtn
                                    color="danger"
                                    size="sm"
                                    title={t('Delete')}
                                    aria-label={t('Delete')}
                                    onClick={() => openDelete(product)}
                                  >
                                    <MDBIcon fas icon="trash-alt" />
                                  </MDBBtn>
                                </MDBBtnGroup>
                              </td>
                            </tr>
                          );
                        })}
                      </MDBTableBody>
                    </MDBTable>
                  </div>
                </>
              )}
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('Delete booth')}
        message={deleteMessage}
        confirmLabel={t('Delete')}
        onConfirm={deleteBlocked ? undefined : confirmDelete}
        onClose={closeDelete}
        busy={deleting}
      />
    </>
  );
}
